type EnvName = 'local' | 'stg' | 'prod' | 'test';

const rawEnv = (import.meta.env.VITE_IA_ENV as string | undefined) ?? 'local';
const IA_ENV: EnvName = (rawEnv === 'staging' ? 'stg' : rawEnv === 'production' ? 'prod' : rawEnv) as EnvName;

export const API_URL_OVERRIDE_KEY = 'dev.apiUrlOverride';

/**
 * Auto-migrate legacy overrides that pointed directly at staging/prod (the
 * cross-origin URLs nginx blocks for `localhost:3000`) onto the local Vite
 * proxy paths. This means anyone whose localStorage still has
 * `https://stg-api.rxdev.co` from an earlier session gets silently moved to
 * `/proxy/stg` on next load, instead of staring down a wall of CORS errors.
 */
const LEGACY_REWRITES: Record<string, string> = {
  'https://stg-api.rxdev.co': '/proxy/stg',
  'https://stg-api.rxdev.co/': '/proxy/stg',
  'https://api.rxfoodapp.com': '/proxy/prod',
  'https://api.rxfoodapp.com/': '/proxy/prod',
};

const readApiOverride = (): string | null => {
  // The API override (and the ApiEnvSwitcher that writes it) is a local
  // dev-server convenience only — its presets resolve to Vite proxy paths
  // (`/proxy/stg`, `/proxy/prod`) that don't exist in a built artifact.
  // Every built bundle (staging + production) is pinned to its VITE_API_URL,
  // so production always talks to https://api.rxfoodapp.com with no opt-out.
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(API_URL_OVERRIDE_KEY);
    if (!raw) return null;
    const migrated = LEGACY_REWRITES[raw];
    if (migrated) {
      window.localStorage.setItem(API_URL_OVERRIDE_KEY, migrated);
      return migrated;
    }
    return raw;
  } catch {
    return null;
  }
};

const buildApiUrl = (): string => {
  const override = readApiOverride();
  if (override) return override;
  return (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:1480';
};

export const config = {
  IA_ENV,
  IS_LOCAL: IA_ENV === 'local',
  IS_DEV: IA_ENV === 'local' || IA_ENV === 'stg',
  IS_PROD: IA_ENV === 'prod',
  IS_TEST: IA_ENV === 'test',

  API_URL: buildApiUrl(),
  HAS_API_OVERRIDE: !!readApiOverride(),
  OAUTH_CLIENT_ID: (import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined) ?? '2',

  SENTRY_DSN: (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? '',
  SENTRY_TUNNEL: (import.meta.env.VITE_SENTRY_TUNNEL as string | undefined) ?? '',

  COUNTLY_APP_KEY: (import.meta.env.VITE_COUNTLY_APP_KEY as string | undefined) ?? '',
  COUNTLY_URL: (import.meta.env.VITE_COUNTLY_URL as string | undefined) ?? '',

  COMMIT_SHA: (import.meta.env.VITE_COMMIT_SHA as string | undefined) ?? 'local',
  BUILD_DATE: (import.meta.env.VITE_BUILD_DATE as string | undefined) ?? 'local',
  get RELEASE() {
    if (this.COMMIT_SHA === 'local' && this.BUILD_DATE === 'local') return 'local';
    return `${this.BUILD_DATE}/${this.COMMIT_SHA.slice(0, 7)}`;
  },
} as const;
